import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { getDashboard } from '../../api/instructorApi';
import PageLoader from '../../components/common/PageLoader';
import { toTitleCase } from '../../utils/format';

const PAGE_SIZE = 20;

export default function InstructorDashboard() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [courses, setCourses] = useState([]);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getDashboard().then(d => {
      setStats(d.stats);
      setCourses(d.courses);
      setPage(1);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered   = courses.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const name = user?.full_name?.split(' ')[0] || 'Instructor';

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Instructor / Dashboard</div>
          <div className="pt">Welcome back, {name} 👋</div>
          <div className="ps">
            {search
              ? `${filtered.length} of ${courses.length} course${courses.length !== 1 ? 's' : ''}`
              : 'Manage your courses and track student progress'}
          </div>
        </div>
        {!loading && courses.length > 0 && (
          <input
            className="li"
            style={{ width: 220, margin: 0 }}
            placeholder="Search by title…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        )}
      </div>

      {/* ── Body ── */}
      <div className="body">
        {/* Stats */}
        <div className="stats">
          <div className="sc">
            <div className="sc-l">Total Students</div>
            <div className="sc-v">{stats?.total_students ?? '—'}</div>
            <div className="sc-s" style={{ color: 'var(--green)' }}>Across all courses</div>
          </div>
          <div className="sc">
            <div className="sc-l">Active Courses</div>
            <div className="sc-v">{stats?.active_courses ?? '—'}</div>
            <div className="sc-s" style={{ color: 'var(--muted)' }}>All courses</div>
          </div>
          <div className="sc">
            <div className="sc-l">Assignments</div>
            <div className="sc-v">{stats?.total_assignments ?? '—'}</div>
            <div className="sc-s" style={{ color: 'var(--muted)' }}>Across all courses</div>
          </div>
        </div>

        {/* My Courses card */}
        <div className="card">
          <div className="ct">My Courses</div>

          {loading ? (
            <PageLoader />
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📚</div>
              <div>{search ? `No courses matching "${search}"` : 'No courses assigned yet'}</div>
            </div>
          ) : (<>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {paginated.map(c => (
                <div
                  key={c.id}
                  className="cc"
                  onClick={() => navigate(`/instructor/courses/${c.id}`)}
                >
                  <div
                    className="cc-thumb"
                    style={{ background: `linear-gradient(135deg, ${c.accent_color}22, ${c.accent_color}44)` }}
                  >
                    <span style={{ fontSize: 44 }}>{c.emoji}</span>
                    <div className="cc-tag">
                      {c.assignments_count} assignment{c.assignments_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="cc-body">
                    <div className="cc-title">{toTitleCase(c.title)}</div>
                    <div className="cc-meta">
                      {c.enrolled_count} students enrolled · {c.modules_published}/{c.modules_total} published
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        className="btn btn-p btn-sm"
                        onClick={e => { e.stopPropagation(); navigate(`/instructor/courses/${c.id}`); }}
                      >
                        Manage
                      </button>
                      <button
                        className="btn btn-o btn-sm"
                        onClick={e => { e.stopPropagation(); navigate(`/instructor/courses/${c.id}/students`); }}
                      >
                        Students
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginTop: 20 }}>
                <button
                  className="btn btn-o btn-sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ← Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    className={`btn btn-sm ${n === page ? 'btn-p' : 'btn-o'}`}
                    onClick={() => setPage(n)}
                    style={{ minWidth: 36 }}
                  >
                    {n}
                  </button>
                ))}

                <button
                  className="btn btn-o btn-sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next →
                </button>

                <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
              </div>
            )}
          </>)}
        </div>
      </div>
    </>
  );
}
